// src/components/TurnSelector/TurnHours.js
import { useEffect, useState } from "react";
import moment from "moment";
import { updateHours } from "../../services/newdaily";
import styles from "./TurnHours.module.css";

const toHHmm = (t) => {
  if (!t) return "";
  const m = moment(t, ["HH:mm:ss", "HH:mm", "H:mm"], true);
  return m.isValid() ? m.format("HH:mm") : "";
};

const SENTINEL = "00:00";

function TimeField({ fieldKey, inputId, initial, fecha, empleadoId, isMobile, turno, onLocalUpdate }) {
  const [value, setValue] = useState(toHHmm(initial) || SENTINEL);

  useEffect(() => {
    setValue(toHHmm(initial) || SENTINEL);
  }, [initial, fecha, fieldKey]);

  const commit = async (val) => {
    if (!/^\d{2}:\d{2}$/.test(val) || val === SENTINEL) return;

    const [h, m] = val.split(":");
    const send = `${h.padStart(2, "0")}:${m.padStart(2, "0")}:00`;

    try {
      await updateHours({
        empleado: empleadoId,
        fecha,
        turno,
        [fieldKey]: send, // MI/MF/TI/TF
      });
    } catch (e) {
      console.error("Error guardando hora:", e);
    }
  };

  const handleFocus = () => {
    const today = moment().format("YYYY-MM-DD");
    if (fecha === today && (value === SENTINEL || value === "")) {
      const now = moment().format("HH:mm");
      setValue(now);
      const [h, m] = now.split(":");
      const send = `${h.padStart(2, "0")}:${m.padStart(2, "0")}:00`;
      onLocalUpdate?.({ [fieldKey]: send });
      commit(now);
    }
  };

  return (
    <span
      className={styles.timeInputWrap}
      data-empty={value === SENTINEL ? "true" : "false"}
    >
      <input
        id={inputId}
        type="time"
        step="60"
        className={styles.timeInput}
        value={value}
        min="00:00"
        max="23:59"
        onFocus={handleFocus}
        inputMode="numeric"
        onChange={(e) => {
          const v = e.target.value;
          setValue(v);
          if (/^\d{2}:\d{2}$/.test(v) && v !== SENTINEL) {
            const [h, m] = v.split(":");
            const send = `${h.padStart(2, "0")}:${m.padStart(2, "0")}:00`;
            onLocalUpdate?.({ [fieldKey]: send });
          } else {
            onLocalUpdate?.({ [fieldKey]: "" });
          }
          if (isMobile) commit(v);
        }}
        onBlur={(e) => {
          if (!isMobile) commit(e.target.value);
        }}
        aria-label={fieldKey}
      />
    </span>
  );
}

const HourCard = ({
  startNode,
  endNode,
  startFilled,
  endFilled,
  startInputId,
  endInputId,
  isMobile,
  muted,
}) => (
  <div className={`${styles.card} ${muted ? styles.muted : ""}`}>
    <span
      className={`${styles.dot} ${startFilled ? styles.filled : styles.hollow} ${styles.topDot}`}
    />
    <span className={styles.connector} />
    <span
      className={`${styles.dot} ${endFilled ? styles.filled : styles.hollow} ${styles.bottomDot}`}
    />

    <label
      htmlFor={isMobile ? startInputId : undefined}
      className={`${styles.rowButton} ${styles.rowTop}`}
      aria-label="Editar hora de inicio"
    >
      <span className={`${styles.label} ${styles.labelTop}`}>Inicio</span>
      <span className={`${styles.time} ${styles.timeTop}`}>{startNode}</span>
    </label>

    <label
      htmlFor={isMobile ? endInputId : undefined}
      className={`${styles.rowButton} ${styles.rowBottom}`}
      aria-label="Editar hora de fin"
    >
      <span className={`${styles.label} ${styles.labelBottom}`}>Fin</span>
      <span className={`${styles.time} ${styles.timeBottom}`}>{endNode}</span>
    </label>
  </div>
);

const TurnHours = ({
  turno,
  mi,
  mf,
  ti,
  tf,
  fecha,
  empleadoId,
  tipoTarde,
  isMobile,
  onLocalUpdate
}) => {
  if (turno !== 1 && turno !== 2) return null;

  const idFor = (key) => `time-${empleadoId}-${fecha}-${key}`;

  const oneBlock = turno === 2 || (turno === 1 && (tipoTarde == null || Number(tipoTarde) === 2));

  if (oneBlock) {
    const startKey = turno === 1 ? "MI" : "TI";
    const endKey = turno === 1 ? "MF" : "TF";
    const startVal = turno === 1 ? mi : ti;
    const endVal = turno === 1 ? mf : tf;

    const startId = idFor(startKey);
    const endId = idFor(endKey);

    return (
      <div className={`${styles.container} ${styles.oneCol}`}>
        <HourCard
          startFilled
          endFilled
          isMobile={isMobile}
          startInputId={startId}
          endInputId={endId}
          startNode={
            <TimeField
              inputId={startId}
              fieldKey={startKey}
              initial={startVal}
              fecha={fecha}
              empleadoId={empleadoId}
              isMobile={isMobile}
              turno={turno}
              onLocalUpdate={onLocalUpdate}
            />
          }
          endNode={
            <TimeField
              inputId={endId}
              fieldKey={endKey}
              initial={endVal}
              fecha={fecha}
              empleadoId={empleadoId}
              isMobile={isMobile}
              turno={turno}
              onLocalUpdate={onLocalUpdate}
            />
          }
        />
      </div>
    );
  }

  const miId = idFor("MI");
  const mfId = idFor("MF");
  const tiId = idFor("TI");
  const tfId = idFor("TF");

  return (
    <div className={`${styles.container} ${styles.twoCols}`}>
      <HourCard
        startFilled
        endFilled={false}
        isMobile={isMobile}
        startInputId={miId}
        endInputId={mfId}
        startNode={
          <TimeField
            inputId={miId}
            fieldKey="MI"
            initial={mi}
            fecha={fecha}
            empleadoId={empleadoId}
            isMobile={isMobile}
            turno={turno}
            onLocalUpdate={onLocalUpdate}
          />
        }
        endNode={
          <TimeField
            inputId={mfId}
            fieldKey="MF"
            initial={mf}
            fecha={fecha}
            empleadoId={empleadoId}
            isMobile={isMobile}
            turno={turno}
            onLocalUpdate={onLocalUpdate}
          />
        }
      />
      <HourCard
        startFilled={false}
        endFilled
        isMobile={isMobile}
        startInputId={tiId}
        endInputId={tfId}
        startNode={
          <TimeField
            inputId={tiId}
            fieldKey="TI"
            initial={ti}
            fecha={fecha}
            empleadoId={empleadoId}
            isMobile={isMobile}
            turno={turno}
            onLocalUpdate={onLocalUpdate}
          />
        }
        endNode={
          <TimeField
            inputId={tfId}
            fieldKey="TF"
            initial={tf}
            fecha={fecha}
            empleadoId={empleadoId}
            isMobile={isMobile}
            turno={turno}
            onLocalUpdate={onLocalUpdate}
          />
        }
      />
    </div>
  );
};

export default TurnHours;
